/**
 * Referral Campaign Management API Hooks
 * React hooks for managing referral campaign data
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pricingApiClient } from './pricingApiClient';
import {
  ReferralCampaign,
  ReferralCampaignDTO,
  ReferralCampaignListQueryParams,
  PagedData,
  ReferralCampaignForm
} from './referralCampaignTypes';
import { ApiResponse } from './types';

/**
 * Hook to fetch a single referral campaign by ID
 * @param id - The referral campaign ID
 * @param options - Hook options
 */
export function useReferralCampaign(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: ReferralCampaign) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<ReferralCampaign>>(`/referral-campaign/${id}`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as ReferralCampaign;
  }, [id]);

  return useQuery<ReferralCampaign>({
    queryKey: ['referral-campaign', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch paginated list of referral campaigns
 * @param params - Query parameters (page, size, sort, search)
 * @param options - Hook options
 */
export function useReferralCampaigns(
  params: ReferralCampaignListQueryParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<ReferralCampaign>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.isActive !== undefined) {
      queryParams.isActive = params.isActive;
    }
    if (params.referrerRewardType) {
      queryParams.referrerRewardType = params.referrerRewardType;
    }
    if (params.referredRewardType) {
      queryParams.referredRewardType = params.referredRewardType;
    }
    if (params.validityDays) {
      queryParams.validityDays = params.validityDays;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }

    const response = await pricingApiClient.get<ApiResponse<PagedData<ReferralCampaign>>>(
      '/referral-campaign/list',
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as PagedData<ReferralCampaign>;
  }, [params.page, params.size, params.sort, params.search, params.isActive, params.referrerRewardType, params.referredRewardType, params.validityDays]);

  return useQuery<PagedData<ReferralCampaign>>({
    queryKey: ['referral-campaigns', params],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch all referral campaigns (no pagination)
 * @param options - Hook options
 */
export function useAllReferralCampaigns(
  options: {
    enabled?: boolean;
    onSuccess?: (data: ReferralCampaign[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<ReferralCampaign[]>>(`/referral-campaign/list-only`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as ReferralCampaign[];
  }, []);

  return useQuery<ReferralCampaign[]>({
    queryKey: ['all-referral-campaigns'],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch active referral campaigns only
 * @param options - Hook options
 */
export function useActiveReferralCampaigns(
  options: {
    enabled?: boolean;
    onSuccess?: (data: ReferralCampaign[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<ReferralCampaign[]>>(`/referral-campaign/active-list-only`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as ReferralCampaign[];
  }, []);

  return useQuery<ReferralCampaign[]>({
    queryKey: ['active-referral-campaigns'],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to create a new referral campaign
 * @param options - Hook options
 */
export function useCreateReferralCampaign(
  options: {
    onSuccess?: (data: ApiResponse<ReferralCampaign>, variables: ReferralCampaignForm) => void;
    onError?: (error: Error, variables: ReferralCampaignForm) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<ReferralCampaign>, Error, ReferralCampaignForm>({
    mutationFn: async (referralCampaignData: ReferralCampaignForm) => {
      const response = await pricingApiClient.post<ApiResponse<ReferralCampaign>>(
        '/referral-campaign',
        referralCampaignData
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate all referral campaign queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['all-referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['active-referral-campaigns'] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to update an existing referral campaign
 * @param options - Hook options
 */
export function useUpdateReferralCampaign(
  options: {
    onSuccess?: (data: ApiResponse<ReferralCampaign>, variables: { id: number; data: ReferralCampaignForm }) => void;
    onError?: (error: Error, variables: { id: number; data: ReferralCampaignForm }) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<ReferralCampaign>, Error, { id: number; data: ReferralCampaignForm }>({
    mutationFn: async ({ id, data }) => {
      const response = await pricingApiClient.put<ApiResponse<ReferralCampaign>>(
        `/referral-campaign/${id}`,
        data
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate all referral campaign queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['all-referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['active-referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['referral-campaign', variables.id] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to delete a referral campaign
 * @param options - Hook options
 */
export function useDeleteReferralCampaign(
  options: {
    onSuccess?: (data: ApiResponse<ReferralCampaign>, referralCampaignId: number) => void;
    onError?: (error: Error, referralCampaignId: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<ReferralCampaign>, Error, number>({
    mutationFn: async (referralCampaignId: number) => {
      const response = await pricingApiClient.delete<ApiResponse<ReferralCampaign>>(
        `/referral-campaign/${referralCampaignId}`
      );
      return response;
    },
    onSuccess: (data, referralCampaignId) => {
      // Invalidate all referral campaign queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['all-referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['active-referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['referral-campaign', referralCampaignId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, referralCampaignId);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to toggle referral campaign active status
 * @param options - Hook options
 */
export function useToggleReferralCampaignStatus(
  options: {
    onSuccess?: (data: ApiResponse<ReferralCampaign>, referralCampaignId: number) => void;
    onError?: (error: Error, referralCampaignId: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<ReferralCampaign>, Error, number>({
    mutationFn: async (referralCampaignId: number) => {
      const response = await pricingApiClient.get<ApiResponse<ReferralCampaign>>(
        `/referral-campaign/toggle-active-status/${referralCampaignId}`
      );
      return response;
    },
    onSuccess: (data, referralCampaignId) => {
      // Invalidate all referral campaign queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['all-referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['active-referral-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['referral-campaign', referralCampaignId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, referralCampaignId);
      }
    },
    onError: options.onError,
  });
}

/**
 * Utility function to format reward display
 */
export function formatRewardDisplay(type: string, value: number): string {
  switch (type) {
    case 'DISCOUNT':
      return `${value}%`;
    case 'CASHBACK':
    case 'CREDIT':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(value);
    case 'FREE_RIDE':
      return `${value} Free Ride${value > 1 ? 's' : ''}`;
    case 'POINTS':
      return `${value} Points`;
    default:
      return `${value} ${type}`;
  }
}

/**
 * Utility function to get reward type display name
 */
export function getRewardTypeDisplay(type: string): string {
  const types: Record<string, string> = {
    'DISCOUNT': 'Discount (%)',
    'CASHBACK': 'Cashback ($)',
    'CREDIT': 'Account Credit ($)',
    'FREE_RIDE': 'Free Rides',
    'POINTS': 'Loyalty Points'
  };
  return types[type] || type;
}